import { type RefObject, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import type { Pages } from "../DataModel.types";
import type { BuilderUIStore } from "../Store";
import type { BuilderUIProviderProps } from "../Store/types";

const ONE_SECOND = 1_000;

export const useDebouncedOnChange = (
  store: RefObject<BuilderUIStore | undefined>,
  onChange: BuilderUIProviderProps["onChange"],
) => {
  const getDebouncedOnChange = useCallback(
    () =>
      debounce(async (pages: Pages) => {
        if (onChange) {
          await onChange(pages);
        }
      }, ONE_SECOND),
    [onChange],
  );

  useEffect(() => {
    if (!onChange || !store.current) return;

    const debouncedOnChange = getDebouncedOnChange();
    const unsubscribe = store.current.subscribe(
      state => state.pages,
      pages => {
        void debouncedOnChange(pages);
      },
    );

    return () => {
      debouncedOnChange.cancel();
      unsubscribe();
    };
  }, [onChange, getDebouncedOnChange, store]);
};
